import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Store, Zap } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const HERO = "https://static.prod-images.emergentagent.com/jobs/d3a3c830-7d3a-49ef-ad18-3f342dc4beda/images/5cf8084027a258ee5ad84419f46482be0641e210312230f79ca61c9e5990c792.png";

export default function Home() {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    api.get("/products?limit=8").then((r) => setProducts(r.data || []));
    api.get("/categories").then((r) => setCats(r.data || []));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={HERO} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end min-h-[70vh]">
          <div className="lg:col-span-8 reveal">
            <span className="badge-neon" data-testid="hero-badge">A marketplace for makers</span>
            <h1 className="font-display font-black text-5xl sm:text-7xl lg:text-8xl tracking-tighter mt-6 leading-[0.9]">
              SELL THE<br />
              <span className="text-[#FF007F] text-glow-pink">GLOW.</span>
              <br />BUY THE <span className="text-[#05D9E8] text-glow-cyan">RARE.</span>
            </h1>
            <p className="mt-6 text-zinc-300 max-w-xl leading-relaxed">
              GlowCart is where independent artists, designers, and makers list one-of-a-kind goods. Pay a tiny fee, hit publish, and let the world discover your work.
            </p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link data-testid="hero-start-selling" to="/seller" className="btn-primary inline-flex items-center gap-2">
                Start selling <ArrowRight className="w-4 h-4" />
              </Link>
              <Link data-testid="hero-browse" to="/browse" className="btn-secondary inline-flex items-center gap-2">
                Browse the drop
              </Link>
            </div>
          </div>

          <div className="lg:col-span-4 hidden lg:flex flex-col gap-4 reveal" style={{animationDelay: ".2s"}}>
            <div className="card-surface p-6">
              <Sparkles className="w-5 h-5 text-[#CCFF00] mb-3" />
              <p className="text-xs uppercase tracking-widest text-zinc-500">Listing fee</p>
              <p className="font-display font-black text-3xl mt-1">$0.50</p>
              <p className="text-xs text-zinc-500 mt-1">per product · or go Pro</p>
            </div>
            <div className="card-surface p-6">
              <Store className="w-5 h-5 text-[#05D9E8] mb-3" />
              <p className="text-xs uppercase tracking-widest text-zinc-500">Pro Seller</p>
              <p className="font-display font-black text-3xl mt-1">$9.99<span className="text-base text-zinc-500">/mo</span></p>
              <p className="text-xs text-zinc-500 mt-1">unlimited listings · zero fees</p>
            </div>
            <div className="card-surface p-6">
              <Sparkles className="w-5 h-5 text-[#CCFF00] mb-3" />
              <p className="text-xs uppercase tracking-widest text-zinc-500">Boost</p>
              <p className="font-display font-black text-3xl mt-1">$2.99</p>
              <p className="text-xs text-zinc-500 mt-1">7 days pinned to top</p>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex items-baseline justify-between mb-10">
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight">Categories</h2>
          <Link to="/categories" className="text-xs uppercase tracking-widest text-[#05D9E8] hover:underline" data-testid="see-all-categories">all</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {cats.slice(0, 8).map((c) => (
            <Link
              key={c.slug}
              to={`/browse?category=${c.slug}`}
              data-testid={`category-tile-${c.slug}`}
              className="card-surface p-6 text-left group"
            >
              <Zap className="w-4 h-4 text-[#FF007F] mb-6" />
              <p className="font-display font-bold text-lg group-hover:text-[#FF007F] transition-colors">{c.name}</p>
              <p className="text-xs text-zinc-500 uppercase tracking-widest mt-1">Explore →</p>
            </Link>
          ))}
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-baseline justify-between mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-2">Just dropped</p>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight">Featured drops</h2>
          </div>
          <Link to="/browse" className="text-xs uppercase tracking-widest text-[#FF007F] hover:underline" data-testid="see-all-products">view all</Link>
        </div>
        {products.length === 0 ? (
          <p className="text-zinc-500 text-sm" data-testid="empty-home-products">No products yet — be the first to list.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {products.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="card-surface p-12 lg:p-16 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#FF007F]/20 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-[#05D9E8]/15 blur-3xl" />
          <div className="relative">
            <h3 className="font-display font-black text-4xl lg:text-5xl tracking-tighter max-w-2xl">
              List your first product in <span className="text-[#FF007F] text-glow-pink">under 2 minutes.</span>
            </h3>
            <p className="text-zinc-400 mt-4 max-w-xl">Upload photos, set a price, pay $0.50 — your product is live. Want unlimited? Subscribe and skip listing fees forever.</p>
            <Link to="/seller" data-testid="cta-start-selling" className="btn-primary inline-flex items-center gap-2 mt-8">
              Open seller dashboard <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
